import { Drawer } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { type ComponentProps, type CSSProperties, type ReactNode } from 'react';
import { SwipeToCloseDrawerContent } from './SwipeToCloseDrawer';

type DrawerProps = ComponentProps<typeof Drawer>;

/** Viewport range where ResponsiveDrawer renders as a bottom sheet. */
export const BOTTOM_SHEET_QUERY = '(max-width: 576px)';

/**
 * True when drawers render as the mobile bottom sheet. Components inside a
 * sheet must not portal floating dropdowns to the body: the sheet is fixed
 * and transformed, and body-portaled overlays compute positions past the end
 * of the document, growing the mobile layout viewport and moving the sheet.
 */
export const useIsBottomSheet = () => useMediaQuery(BOTTOM_SHEET_QUERY);

interface ResponsiveDrawerProps extends DrawerProps {
  /** Desktop size — ignored on mobile where drawer is always full-width bottom sheet */
  size?: DrawerProps['size'];
  /** Height of the bottom sheet on mobile (default: '85%') */
  mobileSize?: DrawerProps['size'];
  /** Always render as a bottom sheet with swipe-to-close, even on desktop */
  forceBottomSheet?: boolean;
  /**
   * Make the drawer body fill the full drawer height on desktop (flex column),
   * so children can use `margin-top: auto` to pin content to the bottom. No-op
   * on mobile, where the bottom sheet already hugs its content height.
   */
  growBody?: boolean;
  /**
   * Mobile bottom sheet only: shrink the sheet to fit short content
   * (default: true). Set false to pin the sheet at its max height so it
   * never resizes while open. No-op on desktop.
   */
  mobileShrinkToContent?: boolean;
}

/**
 * A responsive drawer that renders as a right-side panel on desktop
 * and a swipe-to-close bottom sheet on mobile.
 *
 * Use `forceBottomSheet` to always render as a bottom sheet.
 *
 * Focus is not trapped by default: Mantine's trap focuses the first
 * focusable child on open, which leaves a stray focus ring on whatever
 * button happens to be first. Pass `trapFocus` for drawers that are
 * genuinely modal forms and want keyboard focus contained.
 */
export const ResponsiveDrawer = ({
  children,
  size = 'md',
  mobileSize = '85%',
  styles,
  title,
  onClose,
  forceBottomSheet,
  padding,
  closeOnClickOutside,
  growBody,
  mobileShrinkToContent,
  scrollAreaComponent,
  trapFocus = false,
  ...rest
}: ResponsiveDrawerProps) => {
  const isMobile = useMediaQuery(BOTTOM_SHEET_QUERY);
  const useBottomSheet = forceBottomSheet || isMobile;

  if (useBottomSheet) {
    // Preserve body styles (e.g. fontFamily for NutritionDrawer) on mobile,
    // but strip padding which is managed by SwipeToCloseDrawerContent layout
    const { padding: _p, paddingTop: _pt, paddingBottom: _pb, paddingLeft: _pl, paddingRight: _pr, ...bodyStyle } =
      (styles as Record<string, CSSProperties> | undefined)?.body ?? {} as CSSProperties;

    return (
      <Drawer
        position="bottom"
        size="100%"
        withCloseButton={false}
        title={null}
        onClose={onClose}
        closeOnClickOutside={closeOnClickOutside}
        trapFocus={trapFocus}
        transitionProps={{ duration: 200 }}
        classNames={{ content: 'drawer-keep-radius' }}
        styles={{
          content: { borderBottomLeftRadius: 0, background: 'transparent', boxShadow: 'none', pointerEvents: 'none' },
          header: { display: 'none' },
          body: { height: '100%', padding: 0, display: 'flex', flexDirection: 'column' },
        }}
        {...rest}
      >
        <SwipeToCloseDrawerContent
          onClose={onClose}
          header={title as ReactNode}
          contentStyle={bodyStyle}
          maxHeight={forceBottomSheet ? size || mobileSize : mobileSize}
          allowSwipeClose={closeOnClickOutside !== false}
          shrinkToContent={mobileShrinkToContent}
        >
          {children}
        </SwipeToCloseDrawerContent>
      </Drawer>
    );
  }

  const stylesRecord = styles as Record<string, CSSProperties> | undefined;
  const desktopStyles: Record<string, CSSProperties> | undefined = growBody
    ? {
        ...stylesRecord,
        content: { display: 'flex', flexDirection: 'column', ...stylesRecord?.content },
        body: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, ...stylesRecord?.body },
      }
    : stylesRecord;

  return (
    <Drawer
      position="right"
      size={size}
      title={title}
      onClose={onClose}
      styles={desktopStyles}
      padding={padding}
      scrollAreaComponent={scrollAreaComponent}
      trapFocus={trapFocus}
      {...rest}
    >
      {children}
    </Drawer>
  );
};
